import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import styled from 'styled-components'
import { rem, transparentize } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import TextButton from 'LuxKit/Button/TextButton'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import LuxPlusLandingBenefitCard from './LuxPlusLandingBenefitCard'
import LuxPlusDestinationCarousel from './LuxPlusDestinationCarousel'
import LuxPlusLandingMembershipSection from './LuxPlusLandingMembershipSection'
import LuxPlusLandingPartnerOffersSection from './LuxPlusLandingPartnerOffersSection'
import LuxPlusTestimonialCarousel from './LuxPlusTestimonialCarousel'
import LuxPlusLandingOfferCarousels from './LuxPlusLandingOfferCarousels'
import LuxPlusLandingFaqSection from './LuxPlusLandingFaqSection'
import LuxPlusLandingBottomCTA from './LuxPlusLandingBottomCTA'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import useShouldShowMembershipCta from 'luxPlus/hooks/useShouldShowMembershipCta'
import { getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LuxPlusPriceStack from '../LuxPlusPriceStack'
import Pill from 'LuxKit/Pill'
import LineLockOpenAltIcon from 'LuxKit/Icons/line/LineLockOpenAltIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Divider from 'LuxKit/Divider'
import Image from 'components/Common/Image'
import LineFestivalsIcon from 'LuxKit/Icons/line/LineFestivalsIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'

const HeroImageWrapper = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
  width: 100%;
`

const HeroImage = styled(Image)`
  width: 100%;
  height: ${rem(220)};
  object-fit: cover;
  ${mediaQueryUp.tablet} {
    height: ${rem(380)};
  }

  ${mediaQueryUp.desktop} {
    img {
      transform: scaleX(-1);
    }
  }
`

const HeroOverlayCard = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: column;
    gap: ${rem(8)};
    position: absolute;
    top: ${rem(60)};
    left: ${rem(24)};
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    border-radius: ${props => props.theme.borderRadius.M};
    padding: ${rem(20)};
    max-width: ${rem(280)};
  }
`

const SavePill = styled.div`
  position: absolute;
  top: ${rem(-15)};
  right: ${rem(-6)};
`

const PriceOverlay = styled.div`
  position: absolute;
  top: ${rem(24)};
  right: ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  background-color: ${transparentize(0.15, '#FFFFFF')};
  backdrop-filter: blur(${rem(8)});
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(16)} ${rem(12)};
  box-shadow:
    0 0 ${rem(17)} 0 ${transparentize(0.97, '#000000')},
    0 0 ${rem(5)} 0 ${transparentize(0.96, '#000000')},
    0 0 ${rem(2)} 0 ${transparentize(0.96, '#000000')},
    0 0 ${rem(1)} 0 ${transparentize(0.94, '#000000')};
  ${mediaQueryUp.desktop} {
    top: ${rem(150)};
    left: ${rem(280)};
    right: unset;
  }

`

const MobileTextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const BenefitsGrid = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SpecialOfferText = styled(CaptionTextBlock)`
  color: ${props => props.theme.palette.highlight.primary.darkForeground};
`

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: center;
  }
`

const TranslucentCard = styled.div`
  background:
    linear-gradient(0deg, ${transparentize(0.70, '#FFFFFF')} 0%, ${transparentize(0.70, '#FFFFFF')} 100%),
    ${transparentize(0.40, '#FFFFFF')};
  backdrop-filter: blur(${rem(4)});
  border: 1px solid ${transparentize(0.90, '#FFFFFF')};
  border-radius: ${props => props.theme.borderRadius.M};
  box-shadow: ${props => props.theme.shadow.flat.small};
`

const OverlayTag = styled(TranslucentCard)`
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  padding: ${rem(6)} ${rem(12)};
  font-size: ${rem(14)};
  font-weight: 600;
  border-radius: ${props => props.theme.borderRadius.round};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const OverlayCard = styled(TranslucentCard)`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  white-space: nowrap;
  padding: ${rem(16)} ${rem(20)};
`

const SupportOverlay = styled.div`
  position: absolute;
  bottom: ${rem(16)};
  right: ${rem(12)};
  left: ${rem(12)};
  display: flex;
  justify-content: flex-end;
`

const ChatBubble = styled(TranslucentCard)`
  display: flex;
  align-items: center;
  box-shadow:
    0 0 ${rem(1)} 0 ${transparentize(0.94, '#000000')},
    0 0 ${rem(2)} 0 ${transparentize(0.96, '#000000')},
    0 0 ${rem(5)} 0 ${transparentize(0.96, '#000000')},
    0 0 ${rem(17)} 0 ${transparentize(0.97, '#000000')};
  padding: ${rem(8)} ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const LogoCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.brand.primary.lightest};
`

function LuxPlusLandingAboutSection() {
  const intl = useIntl()
  const { handleBecomeMember, handleStartFreePreview } = useLuxPlusLandingActions()
  const showCta = useShouldShowMembershipCta()
  const isPreview = useAppSelector(getIsFreePreviewLuxPlusMember)
  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)
  const offer = useMemo(
    () => subscriptionProduct.offers?.find(o => !o.joinFeeWaived),
    [subscriptionProduct.offers],
  )

  const breakfastTag = intl.formatMessage({
    id: 'kCl3XmfuUI0A7_6o155r',
    defaultMessage: '+ Breakfast',
    description: 'Overlay tag on benefit card showing bonus breakfast inclusion',
  })
  const cocktailsTag = intl.formatMessage({
    id: 'xrwJv_IBBhsPK_3ThYrh',
    defaultMessage: '+ Cocktails',
    description: 'Overlay tag on benefit card showing bonus cocktails inclusion',
  })
  const memberOnlyAccessLabel = intl.formatMessage({
    id: 'g53lEXV911Lz5cVl4MIe',
    defaultMessage: 'Member only access',
    description: 'Overlay label on benefit card showing exclusive member access',
  })
  const chatBubbleText = intl.formatMessage({
    id: 'MmMW8e8jWtCdSdXgYwRX',
    defaultMessage: 'Hi Taylor, how can I help?',
    description: 'Example chat bubble text on concierge benefit card',
  })

  const benefits = useMemo(() => [
    {
      imageSrc: 'n0e7hjxi1nec5esx3qp',
      imageAlt: intl.formatMessage({
        id: 'DJCQfttjEM1A3a9pEMbU',
        defaultMessage: 'Cocktails and breakfast inclusions',
        description: 'Alt text for the bonus inclusions benefit card image on LuxPlus page',
      }),
      title: intl.formatMessage({
        id: '4-mAn895ot9FSrb2-gFo',
        defaultMessage: 'Bonus inclusions on stays and sailings',
        description: 'Title for the bonus inclusions benefit card on LuxPlus page',
      }),
      description: intl.formatMessage({
        id: '9VBGu8fDw6wc2z9t7pH1',
        defaultMessage: 'Enjoy bonus hotel credit, daily breakfast, onboard spend and other extras.',
        description: 'Description for the bonus inclusions benefit card on LuxPlus page',
      }),
      imageOverlay:
        <CSSBreakpoint min="tablet">
          <OverlayTag style={{ top: '10%', right: '25%' }}>{breakfastTag}</OverlayTag>
          <OverlayTag style={{ bottom: '20%', left: '15%' }}>{cocktailsTag}</OverlayTag>
        </CSSBreakpoint>,
    },
    {
      imageSrc: 'pfc67g7och7mpy9k0lm',
      imageAlt: intl.formatMessage({
        id: 'wgCeRiJtR1B4jJTKDPmj',
        defaultMessage: 'Woman swimming in infinity pool',
        description: 'Alt text for the early access benefit card image on LuxPlus page',
      }),
      title: intl.formatMessage({
        id: 'HS3lRI3xl25DJwBXyC6_',
        defaultMessage: 'Access the best offers before anyone else',
        description: 'Title for the early access benefit card on the LuxPlus landing page',
      }),
      description: intl.formatMessage({
        id: 'HK2FAaZ7XioOiweTu1tV',
        defaultMessage: 'Be first to book our most popular hotels, with early access and member-only offers.',
        description: 'Description for the early access benefit card on LuxPlus page',
      }),
      imageOverlay: <CSSBreakpoint min="tablet">
        <OverlayCard style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <LineLockOpenAltIcon size="M" />
          <BodyText variant="medium" colour="normal-contrast" align="center">{memberOnlyAccessLabel}</BodyText>
        </OverlayCard>
      </CSSBreakpoint>,
    },
    {
      imageSrc: '5z0khcncrahy8qkhzor',
      imageAlt: intl.formatMessage({
        id: 'KLL3quM4uFTZWtfoUBrK',
        defaultMessage: 'Fine dining experience',
        description: 'Alt text for the everyday savings benefit card image on LuxPlus page',
      }),
      title: intl.formatMessage({
        id: 'JjmY5sdY8fvHz3mjn1Js',
        defaultMessage: 'Everyday savings beyond travel',
        description: 'Title for the everyday savings benefit card on LuxPlus page',
      }),
      description: intl.formatMessage({
        id: 'GrgzR3o9t-zfR_gSDE51',
        defaultMessage: 'Enjoy exclusive discounts across dining, experiences and lifestyle brands.',
        description: 'Description for the everyday savings benefit card on LuxPlus page',
      }),
    },
    {
      imageSrc: '4ybcsqdbvhb2vrx0zuf',
      imageAlt: intl.formatMessage({
        id: '_G2Sb7hsk4ygrnMkUPbF',
        defaultMessage: 'Customer support specialist',
        description: 'Alt text for the customer support benefit card image on LuxPlus page',
      }),
      title: intl.formatMessage({
        id: 'B-6kQaZbFVEs3PuSn6Fb',
        defaultMessage: 'Priority access to 24/7 customer support',
        description: 'Title for the customer support benefit card on LuxPlus page',
      }),
      description: intl.formatMessage({
        id: 'GRhbrgpSRfzoAgKaMGSn',
        defaultMessage: 'Dedicated travel specialists available whenever you or your travellers need help.',
        description: 'Description for the customer support benefit card on LuxPlus page',
      }),
      imageOverlay: <CSSBreakpoint min="tablet">
        <SupportOverlay>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <LogoCircle>
              <LuxuryEscapesSmallLogo height={20} />
            </LogoCircle>
            <ChatBubble>{chatBubbleText}</ChatBubble>
          </Group>
        </SupportOverlay>
      </CSSBreakpoint>,
    },
  ], [intl, breakfastTag, cocktailsTag, memberOnlyAccessLabel, chatBubbleText])

  return (<>
    <LayoutContainer>
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={8} horizontalAlign="center">
          <HeadingTextBlock variant="heading2" align="center">
            {intl.formatMessage({
              id: 'GWRseMVnjp0OQpRZjROy',
              defaultMessage: 'Unlimited benefits all year round',
              description: 'Heading for the LuxPlus about section benefit showcase',
            })}
          </HeadingTextBlock>
          {offer && <BodyTextBlock variant="large" colour="normal-contrast" align="center">
            <FormattedMessage
              id="9cmGvEF112Irdim2bhvN"
              defaultMessage="Enjoy member-only benefits for {price} per year, and a one-off joining fee of {joinFee}."
              description="Subheading showing LuxPlus subscription price and joining fee"
              values={{
                price: <FormatCurrency value={offer.price} />,
                joinFee: <FormatCurrency value={offer.joinFee} />,
              }}
            />
          </BodyTextBlock>}
          {offer && <SpecialOfferText variant="medium" align="center" startIcon={<LineFestivalsIcon />}>
            <FormattedMessage
              id="7ZUMSJjcF9tdVIDJ2XIO"
              defaultMessage="<strong>Special offer:</strong> No joining fee when you purchase an eligible {programName} offer (save {joinFee})"
              description="Special offer banner showing joining fee waiver when purchasing an eligible offer"
              values={{
                programName: LUXURY_PLUS.PROGRAM_NAME,
                joinFee: <FormatCurrency value={offer.joinFee} />,
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </SpecialOfferText>}
        </Group>

        <HeroImageWrapper>
          <HeroImage
            id="2sh8mhwtif27qzegy6jl"
            alt={intl.formatMessage({
              id: '0GP4MOWb0JiHW-sZ3NCC',
              defaultMessage: 'Luxury resort sunset pool',
              description: 'Alt text for the hero image in the LuxPlus about section',
            })}
          />
          <HeroOverlayCard>
            <Heading variant="heading4">
              {intl.formatMessage({
                id: 'ykWf-2yMhxejAoJl1h3r',
                defaultMessage: 'Save hundreds with member-only prices',
                description: 'Heading on hero overlay card promoting member pricing',
              })}
            </Heading>
            <BodyText variant="small" colour="light-foreground-alt">
              {intl.formatMessage({
                id: 'C5qckLB7yezQbeJD1FN9',
                defaultMessage: 'Unlock lower prices on hotels, tours and experiences around the world.',
                description: 'Description on hero overlay card about member pricing benefits',
              })}
            </BodyText>
          </HeroOverlayCard>
          <PriceOverlay>
            <SavePill>
              <Pill kind="primary" variant="success">
                <FormattedMessage
                  id="H9AlAHJGA4zhsqTUveV6"
                  defaultMessage="Save {amount}"
                  description="Pill showing example savings amount on the LuxPlus hero image"
                  values={{ amount: <FormatCurrency value={100} format="rounded" /> }}
                />
              </Pill>
            </SavePill>
            <LuxPlusPriceStack
              size="M"
              price={2099}
              memberPrice={1999}
              saleUnit=""
              signDisplay="always"
            />
          </PriceOverlay>
        </HeroImageWrapper>

        <MobileTextSection>
          <Heading variant="heading4">
            {intl.formatMessage({
              id: 'ykWf-2yMhxejAoJl1h3r',
              defaultMessage: 'Save hundreds with member-only prices',
              description: 'Heading on hero overlay card promoting member pricing',
            })}
          </Heading>
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'C5qckLB7yezQbeJD1FN9',
              defaultMessage: 'Unlock lower prices on hotels, tours and experiences around the world.',
              description: 'Description on hero overlay card about member pricing benefits',
            })}
          </BodyText>
        </MobileTextSection>

        <BenefitsGrid>

          {benefits.map((benefit) => (
            <LuxPlusLandingBenefitCard
              key={benefit.title}
              imageSrc={benefit.imageSrc}
              imageAlt={benefit.imageAlt}
              title={benefit.title}
              description={benefit.description}
              imageOverlay={benefit.imageOverlay}
            />
          ))}
        </BenefitsGrid>

        {showCta && <CTASection>
          <TextButton kind="primary" onClick={handleBecomeMember}>
            {intl.formatMessage({
              id: '3-tM77AW0JCHSsgPbV8Y',
              defaultMessage: 'Become a member',
              description: 'CTA button to start LuxPlus membership purchase on about section',
            })}
          </TextButton>
          {!isPreview && (
            <TextButton kind="secondary" onClick={handleStartFreePreview}>
              {intl.formatMessage({
                id: 'UVip3IlIsg0kmlbsfdjr',
                defaultMessage: 'Start free preview',
                description: 'CTA button to start free preview on the LuxPlus about section',
              })}
            </TextButton>
          )}
        </CTASection>}
        <Divider kind="primary" />
      </Group>
    </LayoutContainer>
    <CSSBreakpoint min="tablet">
      <LuxPlusDestinationCarousel />
    </CSSBreakpoint>
    <LuxPlusLandingMembershipSection />
    <LuxPlusTestimonialCarousel />
    <LayoutContainer>
      <LuxPlusLandingPartnerOffersSection />
    </LayoutContainer>
    <LuxPlusLandingOfferCarousels />
    <LuxPlusLandingFaqSection />
    {showCta && <LuxPlusLandingBottomCTA />}
  </>
  )
}

export default LuxPlusLandingAboutSection
